import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/utils/cn';
import type { QuestionOption } from '@/types/frontend';

interface AnswerOptionsProps {
  options: QuestionOption[];
  selectedOptionId?: string;
  onSelect: (optionId: string, customValue?: string) => void;
  questionId?: string;
}

export const AnswerOptions: React.FC<AnswerOptionsProps> = ({
  options,
  selectedOptionId,
  onSelect,
  questionId
}) => {
  const [customPercentage, setCustomPercentage] = useState<string>('');
  const [coverageNotes, setCoverageNotes] = useState<string>('');

  const isPourcentageQuestion = questionId === 'pourcentage_murs_isoles_8';

  const handleOptionSelect = (optionId: string) => {
    if (optionId === 'autre_pourcentage') {
      onSelect(optionId, customPercentage || '100');
    } else {
      onSelect(optionId);
    }
  };

  return (
    <div className="space-y-3">
      {options.map((option) => {
        const isSelected = option.id === selectedOptionId;

        return (
          <div key={option.id}>
            <div
              className={cn(
                'relative flex items-start p-4 rounded-lg border-2 transition-all duration-200 cursor-pointer',
                isSelected
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'
              )}
              onClick={() => handleOptionSelect(option.id)}
            >
              <div className="flex items-center h-5">
                <div
                  className={cn(
                    'w-5 h-5 rounded-full border flex items-center justify-center transition-all',
                    isSelected
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-gray-300'
                  )}
                >
                  {isSelected && <Check className="w-3 h-3 text-white" />}
                </div>
              </div>

              <div className="ml-3 flex-1">
                <label
                  className={cn(
                    'font-medium cursor-pointer',
                    isSelected ? 'text-blue-700' : 'text-gray-900'
                  )}
                >
                  {option.label}
                </label>

                {option.description && (
                  <p className={cn(
                    'text-sm mt-1',
                    isSelected ? 'text-blue-600' : 'text-gray-500'
                  )}>
                    {option.description}
                  </p>
                )}
              </div>
            </div>

            {isSelected && option.id === 'autre_pourcentage' && (
              <div className="mt-3 ml-8 p-4 border-2 border-blue-200 rounded-lg bg-blue-50/50">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pourcentage personnalisé (10% - 100%)
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="10"
                    max="100"
                    step="5"
                    value={customPercentage || '100'}
                    onChange={(e) => {
                      setCustomPercentage(e.target.value);
                      onSelect('autre_pourcentage', e.target.value);
                    }}
                    className="flex-1 h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="w-20 text-right">
                    <input
                      type="number"
                      min="10"
                      max="100"
                      value={customPercentage || '100'}
                      onChange={(e) => {
                        const val = Math.min(Math.max(parseInt(e.target.value) || 10, 10), 100);
                        setCustomPercentage(val.toString());
                        onSelect('autre_pourcentage', val.toString());
                      }}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-center"
                    />
                  </div>
                  <span className="text-lg font-semibold text-blue-700">%</span>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {isPourcentageQuestion && selectedOptionId && (
        <div className="mt-4 p-4 border-2 border-gray-200 rounded-lg bg-gray-50">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Notes optionnelles (façades concernées, zones...)
          </label>
          <input
            type="text"
            placeholder="Ex: Façades nord et est uniquement"
            value={coverageNotes}
            onChange={(e) => setCoverageNotes(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      )}
    </div>
  );
};

export default AnswerOptions;